import { useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

/**
 * Quick views are URL-addressable: `?peek=<kind>:<id>` on any signed-in page opens the matching
 * QuickPanel over that page (PeekHost renders it). The param survives reloads and can be shared;
 * opening pushes one history entry (Back closes the panel), switching between peeks while one is
 * open replaces it, and closing pops back to the page underneath.
 */
export const PEEK_KINDS = ["rule", "run", "post", "contact"] as const;
export type PeekKind = (typeof PEEK_KINDS)[number];
export type PeekRef = { kind: PeekKind; id: string };

const ID = /^[0-9a-z-]{1,64}$/i;

export function parsePeek(raw: unknown): PeekRef | null {
  if (typeof raw !== "string") return null;
  const i = raw.indexOf(":");
  if (i < 1) return null;
  const kind = raw.slice(0, i) as PeekKind;
  const id = raw.slice(i + 1);
  return (PEEK_KINDS as readonly string[]).includes(kind) && ID.test(id) ? { kind, id } : null;
}

export const peekParam = (kind: PeekKind, id: string) => `${kind}:${id}`;

declare module "@tanstack/react-router" {
  interface HistoryState {
    /** Set on the history entry a peek was opened into, so closing can go Back instead of stacking entries. */
    peekPushed?: boolean;
  }
}

type Search = Record<string, unknown>;

export function usePeek() {
  const navigate = useNavigate();
  const router = useRouter();
  const raw = useRouterState({ select: (s) => (s.location.search as Search).peek });
  const pushed = useRouterState({ select: (s) => !!s.location.state.peekPushed });
  const peek = useMemo(() => parsePeek(raw), [raw]);

  const open = useCallback(
    (kind: PeekKind, id: string) => {
      const already = parsePeek((router.state.location.search as Search).peek);
      void navigate({
        to: ".",
        search: ((prev: Search) => ({ ...prev, peek: peekParam(kind, id) })) as never,
        replace: !!already,
        resetScroll: false,
        state: ((prev: Record<string, unknown>) => ({ ...prev, peekPushed: already ? !!router.state.location.state.peekPushed : true })) as never,
      });
    },
    [navigate, router],
  );

  const close = useCallback(() => {
    if (!parsePeek((router.state.location.search as Search).peek)) return;
    if (router.state.location.state.peekPushed && window.history.length > 1) {
      router.history.back();
      return;
    }
    void navigate({
      to: ".",
      search: ((prev: Search) => {
        const { peek: _p, ...rest } = prev;
        return rest;
      }) as never,
      replace: true,
      resetScroll: false,
    });
  }, [navigate, router]);

  return { peek, open, close, pushed };
}
